import {
  DIABETES_CARDIOVASCULAR_TREATMENT_MESSAGES,
  DIABETES_NON_ENDOCRINOLOGY_SHARE,
  DIABETES_RENAL_DRUGS_MESSAGES,
} from './diabetes-data'

export const RESEARCH_LOCALES = ['ru', 'en', 'de', 'es', 'pt', 'uz'] as const

export type ResearchLocale = (typeof RESEARCH_LOCALES)[number]

export const RESEARCH_SLUGS = [
  'diabetes-ai-questions',
  'diabetes-roundtable-2026',
  'doctor-ai-questions',
  'prescription-of-doubt',
  'therapist-profile-boundary',
  'laboratory-requests',
] as const

export type ResearchSlug = (typeof RESEARCH_SLUGS)[number]

export const MAIN_COHORT_ID = 'medical-ai-questions-2026'
export const LAB_COHORT_ID = 'laboratory-requests-2025-2026'
export const DIABETES_COHORT_ID = 'diabetes-messages-2025-2026'
export const ROUNDTABLE_COHORT_ID = 'diabetes-roundtable-2026'

export const RESEARCH_COHORTS = {
  [MAIN_COHORT_ID]: {
    id: MAIN_COHORT_ID,
    status: 'historical',
    isCurrentUsage: false,
    period: {
      precision: 'day',
      start: '2026-02-06',
      end: '2026-03-16',
    },
  },
  [LAB_COHORT_ID]: {
    id: LAB_COHORT_ID,
    status: 'historical',
    isCurrentUsage: false,
    period: {
      precision: 'month',
      start: '2025-12',
      end: '2026-03',
    },
  },
  [DIABETES_COHORT_ID]: {
    id: DIABETES_COHORT_ID,
    status: 'historical',
    isCurrentUsage: false,
    period: {
      precision: 'day',
      start: '2025-12-25',
      end: '2026-08-25',
    },
  },
  [ROUNDTABLE_COHORT_ID]: {
    id: ROUNDTABLE_COHORT_ID,
    status: 'historical',
    isCurrentUsage: false,
    period: {
      precision: 'day',
      start: '2026-09-10',
      end: '2026-09-10',
    },
  },
} as const

export type ResearchCohortId = keyof typeof RESEARCH_COHORTS

export const RESEARCH_METRICS = {
  mainQuestions: { value: 25_068, unit: 'questions' },
  mainUsers: { value: 3_886, unit: 'users' },
  mainCities: { value: 550, unit: 'cities', qualifier: 'plus' },
  answerCompletenessProxy: {
    value: 39.7,
    unit: 'percent',
    scope: 'methodologyProxy',
  },
  drugQuestions: { value: 3_300, unit: 'questions' },
  drugShare: {
    value: 13.2,
    unit: 'percent',
    denominatorMetricId: 'mainQuestions',
    scope: 'mainCohort',
  },
  drugChoiceShare: {
    value: 46,
    unit: 'percent',
    denominatorMetricId: 'drugQuestions',
    scope: 'drugSubset',
  },
  drugSafetyQuestions: { value: 605, unit: 'questions' },
  therapistQuestions: { value: 5_479, unit: 'questions' },
  therapistOutsideProfile: {
    value: 22,
    unit: 'percent',
    qualifier: 'atLeast',
    denominatorMetricId: 'therapistQuestions',
    scope: 'therapistSubset',
  },
  laboratoryCorpus: {
    value: 32_000,
    unit: 'questions',
    qualifier: 'moreThan',
  },
  laboratoryQuestions: { value: 2_816, unit: 'questions' },
  laboratoryDoctors: { value: 1_085, unit: 'doctors' },
  laboratoryMarkers: { value: 128, unit: 'markers' },
  laboratoryInterpretationShare: {
    value: 47.1,
    unit: 'percent',
    denominatorMetricId: 'laboratoryQuestions',
    scope: 'laboratoryAnalyticalSlice',
  },
  laboratoryReferenceShare: {
    value: 19.8,
    unit: 'percent',
    denominatorMetricId: 'laboratoryQuestions',
    scope: 'laboratoryAnalyticalSlice',
  },
  diabetesMessages: { value: 1_541, unit: 'questions' },
  diabetesDoctors: { value: 627, unit: 'doctors' },
  diabetesChats: { value: 1_069, unit: 'chats' },
  diabetesTherapistShare: {
    value: 31.7,
    unit: 'percent',
    denominatorMetricId: 'diabetesMessages',
    scope: 'diabetesMainSample',
  },
  diabetesTreatmentShare: {
    value: 41.1,
    unit: 'percent',
    denominatorMetricId: 'diabetesMessages',
    scope: 'diabetesMainSample',
  },
  diabetesNonEndocrinologyShare: {
    value: DIABETES_NON_ENDOCRINOLOGY_SHARE,
    unit: 'percent',
    qualifier: 'atLeast',
    denominatorMetricId: 'diabetesMessages',
    scope: 'diabetesMainSample',
  },
  diabetesCardiovascularTreatment: {
    value: DIABETES_CARDIOVASCULAR_TREATMENT_MESSAGES,
    unit: 'questions',
    denominatorMetricId: 'diabetesMessages',
    scope: 'diabetesMainSample',
  },
  diabetesRenalDrugs: {
    value: DIABETES_RENAL_DRUGS_MESSAGES,
    unit: 'questions',
    denominatorMetricId: 'diabetesMessages',
    scope: 'diabetesMainSample',
  },
} as const

export type ResearchMetricId = keyof typeof RESEARCH_METRICS

// Verified against the published total and shares in the source material.
// The narrative values 126/110 in the DOCX are arithmetic inconsistencies:
// 771 + 176 + 113 = 1,060, and 176/2,816 = 6.2%, 113/2,816 = 4.0%.
export const LABORATORY_PRIMARY_CARE_BREAKDOWN = {
  total: 1_060,
  therapy: 771,
  pediatrics: 176,
  generalPractice: 113,
} as const

const METHODOLOGY_VERSION = '1.0'

export const RESEARCH_PUBLICATIONS = {
  kommersantMedicalAi: {
    outlet: 'Коммерсантъ',
    url: 'https://www.kommersant.ru/doc/8534020',
  },
  medvestnikClinicalGuidelines: {
    outlet: 'Медвестник',
    url: 'https://medvestnik.ru/content/news/vrachi-rassmatrivaut-klinicheskie-rekomendacii-kak-instrument-proverki.html',
  },
  vademecumLaboratoryRequests: {
    outlet: 'Vademecum',
    url: 'https://vademec.ru/news/2026/04/03/analitiki-pochti-polovina-zaprosov-vrachey-k-ii-svyazana-s-rasshifrovkoy-analizov/',
  },
} as const

export type ResearchReportKind = 'analysis' | 'brief'

export type ResearchReportManifest = {
  slug: ResearchSlug
  kind: ResearchReportKind
  cohortId: ResearchCohortId
  methodologyVersion: typeof METHODOLOGY_VERSION
  sampleMetricIds: readonly ResearchMetricId[]
  findingMetricIds: readonly ResearchMetricId[]
  methodologyMetricIds?: readonly ResearchMetricId[]
  publication?: (typeof RESEARCH_PUBLICATIONS)[keyof typeof RESEARCH_PUBLICATIONS]
  sampleRelation: 'parent-cohort' | 'subset' | 'separate-cohort' | 'event'
  parentReportSlug?: ResearchSlug
  relatedReportSlug?: ResearchSlug
}

export const RESEARCH_REPORTS: readonly ResearchReportManifest[] = [
  {
    slug: 'diabetes-ai-questions',
    kind: 'analysis',
    cohortId: DIABETES_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: ['diabetesMessages', 'diabetesDoctors', 'diabetesChats'],
    findingMetricIds: [
      'diabetesNonEndocrinologyShare',
      'diabetesCardiovascularTreatment',
      'diabetesRenalDrugs',
    ],
    sampleRelation: 'separate-cohort',
  },
  {
    slug: 'diabetes-roundtable-2026',
    kind: 'brief',
    cohortId: ROUNDTABLE_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: [],
    findingMetricIds: [],
    sampleRelation: 'event',
    relatedReportSlug: 'diabetes-ai-questions',
  },
  {
    slug: 'doctor-ai-questions',
    kind: 'analysis',
    cohortId: MAIN_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: ['mainQuestions', 'mainUsers', 'mainCities'],
    findingMetricIds: [],
    publication: RESEARCH_PUBLICATIONS.medvestnikClinicalGuidelines,
    sampleRelation: 'parent-cohort',
  },
  {
    slug: 'prescription-of-doubt',
    kind: 'analysis',
    cohortId: MAIN_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: ['drugQuestions', 'drugShare'],
    findingMetricIds: ['drugChoiceShare', 'drugSafetyQuestions'],
    publication: RESEARCH_PUBLICATIONS.kommersantMedicalAi,
    sampleRelation: 'subset',
    parentReportSlug: 'doctor-ai-questions',
  },
  {
    slug: 'therapist-profile-boundary',
    kind: 'analysis',
    cohortId: MAIN_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: ['therapistQuestions'],
    findingMetricIds: ['therapistOutsideProfile'],
    sampleRelation: 'subset',
    parentReportSlug: 'doctor-ai-questions',
  },
  {
    slug: 'laboratory-requests',
    kind: 'analysis',
    cohortId: LAB_COHORT_ID,
    methodologyVersion: METHODOLOGY_VERSION,
    sampleMetricIds: [
      'laboratoryCorpus',
      'laboratoryQuestions',
      'laboratoryDoctors',
      'laboratoryMarkers',
    ],
    findingMetricIds: [
      'laboratoryInterpretationShare',
      'laboratoryReferenceShare',
    ],
    publication: RESEARCH_PUBLICATIONS.vademecumLaboratoryRequests,
    sampleRelation: 'separate-cohort',
  },
]

export const getResearchReport = (slug: string) =>
  RESEARCH_REPORTS.find((report) => report.slug === slug)

export const isResearchSlug = (slug: string): slug is ResearchSlug =>
  RESEARCH_SLUGS.some((researchSlug) => researchSlug === slug)

export const resolveResearchLocale = (language?: string): ResearchLocale => {
  const locale = language?.split('-').at(0)?.toLowerCase()
  if (
    locale &&
    RESEARCH_LOCALES.some((researchLocale) => researchLocale === locale)
  ) {
    return locale as ResearchLocale
  }
  return 'ru'
}
